/**
 * Shim de compatibilidade: next/router -> react-router-dom
 *
 * Objetivo: permitir que as ~46 telas que importam de "next/router" rodem sob o
 * Vite SEM alteracao, tornando a migracao incremental em vez de big-bang. O
 * alias esta em vite.config.ts (resolve.alias['next/router']).
 *
 * Quando uma tela for migrada de verdade, troque o import por
 * `useNavigate`/`useParams` do react-router e remova o uso do shim. Este arquivo
 * pode ser apagado quando nao houver mais nenhum import de "next/router".
 */
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

type Url = string | { pathname?: string; query?: Record<string, any> };

function urlParaString(url: Url): string {
  if (typeof url === 'string') return url;
  const base = url?.pathname ?? '';
  const q = url?.query
    ? '?' + new URLSearchParams(Object.entries(url.query).map(([k, v]) => [k, String(v)])).toString()
    : '';
  return `${base}${q}`;
}

export function useRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    // No Next, `query` mistura parametros de rota E querystring. Reproduzimos
    // isso para os acessos existentes (query.name, query.codigo, query.copiarDe...)
    // continuarem funcionando sem mudanca.
    const query: Record<string, any> = { ...params };
    searchParams.forEach((valor, chave) => {
      query[chave] = valor;
    });

    return {
      query,
      pathname: location.pathname,
      asPath: `${location.pathname}${location.search}`,
      route: location.pathname,
      isReady: true,
      push: (url: Url) => {
        navigate(urlParaString(url));
        return Promise.resolve(true);
      },
      replace: (url: Url) => {
        navigate(urlParaString(url), { replace: true });
        return Promise.resolve(true);
      },
      back: () => navigate(-1),
      forward: () => navigate(1),
      // O Next recarrega a rota atual; no react-router o equivalente pratico e
      // recarregar a pagina. Varias telas usam reload() apos salvar.
      reload: () => window.location.reload(),
      prefetch: () => Promise.resolve(),
      events: {
        on: () => undefined,
        off: () => undefined,
        emit: () => undefined,
      },
    };
  }, [navigate, location.pathname, location.search, params, searchParams]);
}

/**
 * Alguns arquivos fazem `import Router from 'next/router'` e chamam
 * Router.push(...) FORA de componente (ex.: _app.tsx). Fora do contexto do
 * react-router nao existe navigate, entao caimos para navegacao nativa.
 */
const Router = {
  push: (url: Url) => {
    window.location.assign(urlParaString(url));
    return Promise.resolve(true);
  },
  replace: (url: Url) => {
    window.location.replace(urlParaString(url));
    return Promise.resolve(true);
  },
  reload: () => window.location.reload(),
  back: () => window.history.back(),
  events: { on: () => undefined, off: () => undefined, emit: () => undefined },
};

export default Router;
export { Router };
